// pages/public/SharedAssessment.jsx
// Landing page when someone clicks a link Steve shared
// Validates the link then goes straight to assessment

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAssessment } from '../../context/AssessmentContext';
import axios from 'axios';

const SharedAssessment = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { captureUser, loading } = useAssessment();

  const [linkData, setLinkData] = useState(null);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', jobTitle: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    validateLink();
  }, [token]);

  const validateLink = async () => {
    try {
      const response = await axios.get(`/enterprise/shared-links/validate/${token}`);
      if (response.data.success) {
        setLinkData(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'This link is invalid or has expired.');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) {
      setError('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const result = await captureUser({
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        jobTitle: form.jobTitle,
        companyName: linkData?.companyName || '',
        companyId: linkData?.companyId || null,
        sharedLinkToken: token,
        sharedLinkId: linkData?.linkId,
      });

      if (result?.success) {
        navigate('/assessment');
      } else {
        setError(result?.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (validating) {
    return (
      <div style={s.loadingPage}>
        <div style={s.spinner} />
        <p style={s.loadingText}>Verifying your access link...</p>
      </div>
    );
  }

  if (error && !linkData) {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <div style={s.logo}>
            <span style={s.logoExec}>EXECUTIVE</span>
            <span style={s.logoSpring}>Springboard</span>
          </div>
        </div>
        <div style={s.errorContainer}>
          <div style={s.errorIcon}>❌</div>
          <h1 style={s.errorTitle}>Link Not Valid</h1>
          <p style={s.errorText}>{error}</p>
          <p style={s.errorText}>Please contact Executive Springboard for a new link.</p>
          <button style={s.btnHome} onClick={() => navigate('/')}>
            Go to Home Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.logo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
      </div>

      <div style={s.container} className="esb-container">
        {/* Access badge */}
        <div style={s.accessBadge}>
          <span style={s.accessIcon}>🔓</span>
          <span style={s.accessText}>
            You have been given complimentary access to this assessment
            {linkData?.companyName ? ` by ${linkData.companyName}` : ' by Executive Springboard'}
          </span>
        </div>

        <div style={s.card} className="esb-card-lg">
          <h1 style={s.title}>Welcome to Executive Springboard</h1>
          <p style={s.subtitle}>
            You have been invited to complete a leadership assessment.
            Enter your details below to get started. No payment required.
          </p>

          {error && <div style={s.errorBox}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div style={s.row}>
              <div style={s.formGroup}>
                <label style={s.label}>First Name *</label>
                <input
                  type="text"
                  style={s.input}
                  value={form.firstName}
                  onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))}
                  disabled={submitting}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Last Name *</label>
                <input
                  type="text"
                  style={s.input}
                  value={form.lastName}
                  onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))}
                  disabled={submitting}
                />
              </div>
            </div>

            <div style={s.formGroup}>
              <label style={s.label}>Email Address *</label>
              <input
                type="email"
                style={s.input}
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                disabled={submitting}
              />
            </div>

            <div style={s.formGroup}>
              <label style={s.label}>Job Title <span style={s.optional}>(optional)</span></label>
              <input
                type="text"
                style={s.input}
                value={form.jobTitle}
                onChange={e => setForm(p => ({ ...p, jobTitle: e.target.value }))}
                disabled={submitting}
              />
            </div>

            <button type="submit" style={s.btnSubmit} disabled={submitting || loading}>
              {submitting ? 'Starting...' : 'Start My Assessment →'}
            </button>
          </form>

          <p style={s.privacy}>
            Your information is kept confidential. No payment is required.
          </p>
        </div>
      </div>
    </div>
  );
};

const s = {
  page: { minHeight: '100vh', backgroundColor: 'var(--color-off-white)', fontFamily: 'var(--font-primary)' },
  loadingPage: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' },
  spinner: { width: '40px', height: '40px', border: '4px solid var(--color-light-gray)', borderTopColor: 'var(--color-teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingText: { fontSize: '15px', color: 'var(--color-text-light)' },
  header: { backgroundColor: 'var(--color-primary)', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center' },
  logo: { display: 'flex', flexDirection: 'column', gap: '1px' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#FFFFFF', lineHeight: 1 },
  container: { maxWidth: '540px', margin: '0 auto', padding: '40px 24px' },
  accessBadge: { display: 'flex', gap: '10px', alignItems: 'center', backgroundColor: '#E8F8EF', border: '1px solid #A9DFBF', borderRadius: '100px', padding: '10px 16px', marginBottom: '20px' },
  accessIcon: { fontSize: '16px' },
  accessText: { fontSize: '13px', color: '#1E8449', fontWeight: '500' },
  card: { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)', padding: '40px' },
  title: { fontSize: '24px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '8px' },
  subtitle: { fontSize: '15px', color: 'var(--color-text-light)', lineHeight: '1.6', marginBottom: '24px' },
  errorBox: { backgroundColor: 'var(--color-error-bg)', color: 'var(--color-error)', border: '1px solid #F5C6CB', borderRadius: '8px', padding: '12px 16px', fontSize: '14px', marginBottom: '16px' },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '600', color: 'var(--color-text)', marginBottom: '6px' },
  optional: { fontWeight: '400', color: 'var(--color-text-light)' },
  input: { width: '100%', padding: '12px 14px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '15px', color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box' },
  btnSubmit: { width: '100%', backgroundColor: 'var(--color-teal)', color: '#fff', padding: '14px', borderRadius: '8px', border: 'none', fontSize: '16px', fontWeight: '600', cursor: 'pointer' },
  privacy: { fontSize: '12px', color: 'var(--color-text-light)', textAlign: 'center', marginTop: '16px' },
  errorContainer: { maxWidth: '480px', margin: '80px auto', textAlign: 'center', padding: '24px' },
  errorIcon: { fontSize: '48px', marginBottom: '16px' },
  errorTitle: { fontSize: '24px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '12px' },
  errorText: { fontSize: '15px', color: 'var(--color-text-light)', marginBottom: '8px' },
  btnHome: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '12px 28px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginTop: '16px' },
};

export default SharedAssessment;
